import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme
} from "@mui/material";

import Link from "../../../atoms/mui/Link";

const STATUS_LABELS = {
  complete: "Complete",
  "initial-estimate-ready": "Initial estimate ready",
  partial: "In progress",
  none: "Not started"
};

const CTA_LABELS = {
  complete: "Review estimate",
  "initial-estimate-ready": "Complete estimate",
  partial: "Continue estimate",
  none: "Start estimate"
};

const EstimateProgressCard = ({
  percent,
  year,
  surveyStatus,
  completenessSummary
}) => {
  const { palette } = useTheme();
  const safePercent = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const statusLabel = STATUS_LABELS[surveyStatus] || "In progress";
  const ctaLabel = CTA_LABELS[surveyStatus] || CTA_LABELS.partial;
  const href = year
    ? `/explorer/emissions-survey?year=${year}`
    : "/explorer/emissions-survey";

  const completedCount = Number(completenessSummary?.completedCount) || 0;
  const totalCount = Number(completenessSummary?.totalCount) || 0;
  const resolvedCount =
    Number(completenessSummary?.resolvedCount) || completedCount;
  const pendingCount =
    Number(completenessSummary?.pendingCount) || totalCount - resolvedCount;
  const showProgressDetail = totalCount > 0;
  const hasRemainingSections = showProgressDetail && pendingCount > 0;
  const remainingNote =
    surveyStatus === "initial-estimate-ready"
      ? "Complete the remaining sections to finalize your footprint."
      : "Finish the survey to lock in your footprint.";

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flex: 1,
        minWidth: 240
      }}
    >
      <Typography variant="subtitle1" color="textPrimary">
        Estimate progress
      </Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Box position="relative" display="inline-flex">
          <CircularProgress
            variant="determinate"
            value={100}
            size={72}
            sx={{ color: palette.backgroundGray.main }}
          />
          <CircularProgress
            variant="determinate"
            value={safePercent}
            size={72}
            sx={{ color: palette.secondary.main, position: "absolute", left: 0 }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Typography variant="subtitle2" color="textPrimary">
              {`${safePercent}%`}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="textPrimary">
            {statusLabel}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {year ? `Reporting year ${year}` : "Pick a year to begin"}
          </Typography>
        </Box>
      </Box>
      {showProgressDetail && (
        <Typography variant="body2" color="textPrimary">
          {`${safePercent}% complete (${resolvedCount} of ${totalCount} sections)`}
        </Typography>
      )}
      {hasRemainingSections && (
        <Typography variant="caption" color="textSecondary">
          {remainingNote}
        </Typography>
      )}
      <Link href={href} style={{ textDecoration: "none" }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          sx={{ color: palette.common.white }}
        >
          {ctaLabel}
        </Button>
      </Link>
    </Paper>
  );
};

export default EstimateProgressCard;
